import { ReleaseType } from 'types/releases';
import { useResource } from 'app/utils/hooks';
import {
  fetchLanguageCodesDDLEffect,
  fetchSongDDLEffect,
} from 'app/store/SongList/effects';
import {
  getLanguageCodesSelector,
  getSongDDLSelector,
} from 'app/store/SongList/selectors';
import { useAppDispatch, useAppSelector } from 'app/store';
import {
  getReleaseVersion,
  getVersionSyncStatus,
} from 'app/store/Releases/selectors';
import { syncVersionEffect } from 'app/store/Releases/effects';
import { LabelV2VersionDetailsComponent } from './LabelV2VersionDetailsComponent';
import { LabelV2VersionFormLoader } from '../LabelV2VersionFormLoader';
import {
  useFetchPrimaryAudio,
  useReplaceAudioVersion,
  useUpdateAudioDetails,
  useUpdateVersion,
  useValidations,
} from '../hooks';
import { getPreferredAudioSelectionOption } from '../TuneCoreVersionDetailsForm/utils';

export const LabelV2VersionDetailsForm = () => {
  const dispatch = useAppDispatch();

  const releaseVersion = useAppSelector(getReleaseVersion);
  const isSyncing =
    useAppSelector((state) =>
      getVersionSyncStatus(state, releaseVersion?.song.versionGuid || '')
    ) || false;

  const {
    primaryAudioFetching,
    setPrimaryAudioFetching,
    primaryAudio,
    setPrimaryAudio,
  } = useFetchPrimaryAudio();

  const { replaceAudioVersion, isAudioReplacing } = useReplaceAudioVersion(
    setPrimaryAudioFetching
  );

  const [songDDL, songDDLLoading] = useResource(
    fetchSongDDLEffect,
    getSongDDLSelector
  );

  const [languageCodes, languageCodesLoading] = useResource(
    fetchLanguageCodesDDLEffect,
    getLanguageCodesSelector
  );

  const {
    validationsLoading,
    versionValidations,
    versionSongValidations,
    fetchAudioDetailsValidations,
    updateVersionAudioValidations,
  } = useValidations();

  const { updateAudioDetails } = useUpdateAudioDetails(
    setPrimaryAudio,
    primaryAudio
  );

  const { updateVersion } = useUpdateVersion(ReleaseType.LABEL_V2);

  const syncSongVersion = async () => {
    await dispatch(
      syncVersionEffect({
        versionGuid: releaseVersion?.song.versionGuid as string,
        songGuid: releaseVersion?.metadata.songGuid as string,
      })
    );
  };

  if (
    languageCodesLoading ||
    songDDLLoading ||
    validationsLoading ||
    isSyncing
  ) {
    return <LabelV2VersionFormLoader />;
  }

  return languageCodes &&
    versionSongValidations &&
    releaseVersion &&
    versionValidations ? (
    <LabelV2VersionDetailsComponent
      fetchAudioDetailsValidations={fetchAudioDetailsValidations}
      updateAudioValidations={updateVersionAudioValidations}
      songDDL={songDDL}
      isAudioReplacing={isAudioReplacing}
      isSyncing={isSyncing}
      languageCodes={languageCodes}
      primaryAudio={getPreferredAudioSelectionOption(primaryAudio)}
      primaryAudioFetching={primaryAudioFetching}
      releaseVersion={releaseVersion}
      replaceAudioVersion={replaceAudioVersion}
      syncSongVersion={syncSongVersion}
      updateAudioDetails={updateAudioDetails}
      updateVersion={updateVersion}
      versionSongValidations={versionSongValidations}
      versionValidations={versionValidations}
    />
  ) : null;
};
